'use client';

import { ensureIsFromArr } from '@eliadmin/core/src/utils/misc-utils';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Core override – the four steps of the NajTonery checkout (Figma „Nakupny proces“ 159:24007) instead of
 * the core two. The core values stay (`detail`, `billing`), so links and redirects that the core
 * builds keep working; `?cs=` is the step in the URL like in the core.
 */
export enum CartStep {
  DETAIL = 'detail',
  SHIPPING = 'shipping',
  ORDER = 'billing',
  SUMMARY = 'summary',
}

export const CART_STEPS = [CartStep.DETAIL, CartStep.SHIPPING, CartStep.ORDER, CartStep.SUMMARY];

const CART_STEPPER_EVENT = 'ea:cart-step-change';

function subscribe(onStoreChange: () => void) {
  window.addEventListener('popstate', onStoreChange);
  window.addEventListener(CART_STEPPER_EVENT, onStoreChange);

  return () => {
    window.removeEventListener('popstate', onStoreChange);
    window.removeEventListener(CART_STEPPER_EVENT, onStoreChange);
  };
}

function getSearchSnapshot() {
  return window.location.search;
}

export function useCartStepper() {
  const search = useSyncExternalStore(subscribe, getSearchSnapshot, () => '');
  const searchParams = new URLSearchParams(search);
  const currentStep = ensureIsFromArr(
    searchParams.get('cs') as CartStep,
    Object.values(CartStep),
    CartStep.DETAIL,
  );

  /** `replace` for corrections (a step that is not reachable yet), so Back does not loop. */
  const setStep = useCallback((step: CartStep, { replace = false }: { replace?: boolean } = {}) => {
    const url = new URL(window.location.href);
    if (step === CartStep.DETAIL) url.searchParams.delete('cs');
    else url.searchParams.set('cs', step);
    if (replace) window.history.replaceState(window.history.state, '', url);
    else window.history.pushState(window.history.state, '', url);
    window.dispatchEvent(new Event(CART_STEPPER_EVENT));
    window.scrollTo({ top: 0 });
  }, []);

  return {
    currentStep,
    setStep,
  };
}
